import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import toast from 'react-hot-toast';

const NAV = [
  { label: 'Dashboard',         to: '/admin/dashboard',            icon: '📊' },
  { label: 'Workers',           to: '/admin/workers',              icon: '👷' },
  { label: 'Employers',         to: '/admin/employers',            icon: '🏢' },
  { label: 'Requirements',      to: '/admin/requirements',         icon: '📋' },
  { label: 'Payments',          to: '/admin/payments',             icon: '💰' },
  { label: 'Notifications',     to: '/admin/notifications',        icon: '🔔' },
  { label: 'Matching Weights',  to: '/admin/matching',             icon: '⚖️' },
  { label: 'Audit Logs',        to: '/admin/audit-logs',           icon: '📝' },
  { label: 'Settings',          to: '/admin/settings',             icon: '⚙️' },
];

export default function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const logout   = useAuthStore((s) => s.logout);
  const [mobileOpen, setMobileOpen] = useState(false);

  async function handleLogout() {
    try { await api.post('/auth/logout'); } catch {}
    logout();
    navigate('/login');
    toast.success('Logged out');
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Top header — always visible */}
      <header className="bg-gray-900 text-white sticky top-0 z-40 h-14 flex items-center px-4 justify-between shadow">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden p-1 text-gray-300 hover:text-white text-xl leading-none"
            aria-label="Open menu"
          >
            ☰
          </button>
          <div>
            <span className="font-bold text-lg">naukarihaat.com</span>
            <span className="text-xs text-gray-400 ml-2 hidden sm:inline">Admin Panel</span>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="text-sm text-gray-400 hover:text-white flex items-center gap-1.5"
        >
          🚪 <span className="hidden sm:inline">Logout</span>
        </button>
      </header>

      <div className="flex flex-1">
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex flex-col w-60 bg-gray-900 text-white fixed top-14 bottom-0">
          <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  location.pathname.startsWith(item.to)
                    ? 'bg-brand-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <span>{item.icon}</span>
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-gray-700">
            <button
              onClick={handleLogout}
              className="w-full text-left text-sm text-gray-400 hover:text-white flex items-center gap-2"
            >
              🚪 Logout
            </button>
          </div>
        </aside>

        {/* Mobile Drawer */}
        {mobileOpen && (
          <div
            className="md:hidden fixed inset-0 z-50 bg-black/60"
            onClick={() => setMobileOpen(false)}
          >
            <div
              className="bg-gray-900 text-white w-72 h-full flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-gray-700 flex items-center justify-between">
                <div>
                  <p className="font-bold text-lg">naukarihaat.com</p>
                  <p className="text-xs text-gray-400 mt-0.5">Admin Panel</p>
                </div>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="text-gray-400 hover:text-white text-xl"
                  aria-label="Close menu"
                >
                  ✕
                </button>
              </div>
              <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
                {NAV.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-colors ${
                      location.pathname.startsWith(item.to)
                        ? 'bg-brand-600 text-white'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`}
                  >
                    <span>{item.icon}</span>
                    {item.label}
                  </Link>
                ))}
              </nav>
              <div className="p-4 border-t border-gray-700">
                <button
                  onClick={handleLogout}
                  className="w-full text-left text-sm text-gray-400 hover:text-white flex items-center gap-2"
                >
                  🚪 Logout
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 md:ml-60 p-4 md:p-6 pb-20 md:pb-6 min-h-[calc(100vh-3.5rem)]">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Nav — first 4 items + More */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 flex z-40">
        {NAV.slice(0, 4).map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={`flex-1 flex flex-col items-center py-2 text-xs ${
              location.pathname.startsWith(item.to) ? 'text-brand-400' : 'text-gray-400'
            }`}
          >
            <span className="text-lg">{item.icon}</span>
            {item.label.split(' ')[0]}
          </Link>
        ))}
        <button
          onClick={() => setMobileOpen(true)}
          className="flex-1 flex flex-col items-center py-2 text-xs text-gray-400"
        >
          <span className="text-lg">☰</span>
          More
        </button>
      </nav>
    </div>
  );
}
